'use client';
import React from 'react';
import { LanguageType, LanguageDirection } from '@/lib/types';

type SecondaryButtonStyle = 'neutral' | 'accent';

interface SecondaryButtonProps {
	size: number
	text: string
	disabled?: boolean
	loading?: boolean
	language?: LanguageType,
	style?: SecondaryButtonStyle,
	radius?: number,
	customOutline?: string,
	onClick?: () => void,
	backgroundColor?: string,
	isLandingPage?: boolean,
}

const SIZE_TO_PADDING: Record<number, number> = {
	40: 0.25,
	52: 0.5,
	56: 0.5,
	64: 0.625,
};

const SIZE_TO_RADIUS: Record<number, number> = {
	40: 12,
	52: 16,
	56: 24,
	64: 32
};

const SIZE_TO_TEXT_PADDING_X: Record<number, string> = {
	40: '1rem',
	52: '1rem',
	56: '1.25rem',
	64: '1.25rem',
};

const SIZE_TO_TEXT_PADDING_Y: Record<number, string> = {
	40: '0.375rem',
	52: '0.375rem',
	56: '0.5rem',
	64: '0.5rem',
};

const SIZE_TO_TEXT_STYLE: Record<number, string> = {
	40: 'text-14-fixed--alexandria',
	52: 'text-16-fixed--alexandria',
	56: 'text-16-fixed--alexandria',
	64: 'text-18-fixed--alexandria',
};

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
	english: "ltr",
	arabic: "rtl"
}

const STYLE_TO_BG: Record<SecondaryButtonStyle, string> = {
	neutral: "bg-bg-light-primary hover:bg-bg-light-tertiary focus:bg-bg-light-tertiary disabled:bg-bg-disabled",
	accent: "bg-theme-yellow-primary-base hover:bg-theme-yellow-primary-dark focus:bg-theme-yellow-primary-base disabled:bg-bg-disabled"
}

const STYLE_TO_OUTLINE: Record<SecondaryButtonStyle, string> = {
	neutral: "outline-stroke-soft-200 hover:outline-stroke-soft-200 focus:outline-theme-yellow-primary-base disabled:outline-stroke-disabled",
	accent: "outline-theme-yellow-primary-light hover:outline-theme-yellow-primary-base focus:outline-theme-yellow-primary-light disabled:outline-stroke-disabled"
}

const STYLE_TO_TEXT_COLOR: Record<SecondaryButtonStyle, string> = {
	neutral: "text-text-primary hover:text-text-quaternary group-hover:text-text-quaternary focus:text-text-primary group-focus:text-text-primary disabled:text-text-disabled disabled:hover:text-text-disabled group-disabled:text-text-disabled group-disabled:hover:text-text-disabled",
	accent: "text-text-reversed hover:text-text-reversed group-hover:text-text-reversed focus:text-text-reversed group-focus:text-text-reversed disabled:text-text-disabled disabled:hover:text-text-disabled group-disabled:text-text-disabled group-disabled:hover:text-text-disabled"
}

const STYLE_TO_SHADOW: Record<SecondaryButtonStyle, string> = {
	neutral: "",
	// accent: "shadow-sunshine-primary-glow focus:shadow-important-focus disabled:shadow-none hover:shadow-none"
	accent: "shadow-sunshine-primary-glow focus:shadow-none disabled:shadow-none hover:shadow-none"
}

const STYLE_TO_SPINNER_COLOR: Record<SecondaryButtonStyle, string> = {
	neutral: "text-text-primary",
	accent: "text-text-reversed",
}

const SecondaryButton: React.FC<SecondaryButtonProps> = ({
	size = 64,
	text,
	disabled = false,
	loading = false,
	language = "english",
	style = "neutral",
	radius,
	customOutline,
	onClick,
	backgroundColor,
	isLandingPage = false,
}) => {

	const spinnerPx = Math.round(size * 0.3);

		// TODO: remove backgroundColor prop

	// When isLandingPage and accent style, don't apply disabled visual styling
	const shouldSkipDisabledStyling = isLandingPage && style === "accent" && disabled;

	// Build background classes conditionally
	const getBackgroundClasses = () => {
		if (shouldSkipDisabledStyling) {
			// Remove disabled background styling
			return style === "accent" 
				? "bg-theme-yellow-primary-base hover:bg-theme-yellow-primary-dark focus:bg-theme-yellow-primary-base"
				: STYLE_TO_BG[style];
		}
		return STYLE_TO_BG[style];
	};

	// Build outline classes conditionally
	const getOutlineClasses = () => {
		if (customOutline) return customOutline;
		if (shouldSkipDisabledStyling) {
			// Remove disabled outline styling
			return style === "accent"
				? "outline-theme-yellow-primary-light hover:outline-theme-yellow-primary-base focus:outline-theme-yellow-primary-light"
				: STYLE_TO_OUTLINE[style];
		}
		return STYLE_TO_OUTLINE[style];
	};

	// Build shadow classes conditionally
	const getShadowClasses = () => {
		if (shouldSkipDisabledStyling && style === "accent") {
			// Keep shadow even when disabled
			return "shadow-sunshine-primary-glow focus:shadow-none hover:shadow-none";
		}
		return STYLE_TO_SHADOW[style];
	};

	// Build text color classes conditionally
	const getTextColorClasses = () => {
		if (shouldSkipDisabledStyling) {
			// Remove disabled text color styling
			return style === "accent"
				? "text-text-reversed hover:text-text-reversed group-hover:text-text-reversed focus:text-text-reversed group-focus:text-text-reversed"
				: STYLE_TO_TEXT_COLOR[style];
		}
		return STYLE_TO_TEXT_COLOR[style];
	};

	return (
		<button
			style={{
				height: `${size}px`,
				padding: `${SIZE_TO_PADDING[size]}rem`,
				borderRadius: `${radius ?? SIZE_TO_RADIUS[size]}px`,
			}}
			// focus:outline-none focus:ring-0
			disabled={disabled}
			aria-busy={loading}
			onClick={loading ? undefined : onClick}
			// z-10
			className={`
			w-full
			group
			cursor-pointer
			disabled:cursor-not-allowed
			${loading ? 'pointer-events-none cursor-wait' : ''}
			flex items-center justify-center

			whitespace-nowrap

			transition-[background-color, border, shadow] duration-300 ease-[ease]

			outline-main 
			${getOutlineClasses()}
			${getShadowClasses()}
			${getBackgroundClasses()}
			${backgroundColor ? backgroundColor : ''}
			`}>
			<p
				style={{
					direction: LANGUAGE_TO_DIRECTION[language],
					paddingTop: SIZE_TO_TEXT_PADDING_Y[size],
					paddingBottom: SIZE_TO_TEXT_PADDING_Y[size],
					paddingInlineStart: SIZE_TO_TEXT_PADDING_X[size],
					paddingInlineEnd: SIZE_TO_TEXT_PADDING_X[size],
				}}
				className={`
					w-fit
					
					${getTextColorClasses()}

					${SIZE_TO_TEXT_STYLE[size]}
				`}
			>
				{text}
			</p>
			{loading && (
				<span
					aria-hidden
					style={{ width: `${spinnerPx / 16}rem`, height: `${spinnerPx / 16}rem`, marginInline: '0.25rem' }}
					className={`
						flex-none rounded-full border-2 border-current border-t-transparent
						animate-spin motion-reduce:animate-none
						${STYLE_TO_SPINNER_COLOR[style]}
					`}
				/>
			)}
		</button >
	);
}


export default SecondaryButton;
